/**
 * WHEN THE EXISTING HOUSE TURNS OUT TO BE DIFFERENT (spec §60H).
 *
 * A renovation always starts with a survey that is a little bit wrong. Half
 * way through planning, the user notices the dining room has a window nobody
 * drew — so they go back to Existing and add it. Every Proposed plan was
 * copied from Existing before that, and should now have that window too,
 * because it was always there in the real house.
 *
 * The rule, in renovation language rather than version-control language:
 *
 *   A discovery about the existing house flows forward into the plans,
 *   UNLESS the plan has already made a decision about that very thing.
 *
 * So:
 *   • something newly found in Existing appears in each plan that has no
 *     opinion about it;
 *   • something corrected in Existing updates the copy in a plan that has
 *     not touched it;
 *   • something the plan deliberately changed, moved or removed is left
 *     exactly as the plan says, and the user is told there is a difference
 *     to look at;
 *   • anything else is a question, and questions are asked, never guessed.
 *
 * Pure and deterministic: this module decides, and returns those decisions.
 * Applying them is the coordinator's job, and reviewing them is the user's.
 * Matching uses the SAME root lineage the diff engine uses (originId ?? id).
 */
import type { EntityChange } from "./operations";
import type { PlanModel, PlanObject, WallOpening } from "./model";
import { activeObjects, activeOpenings, activeWalls, nodeOf } from "./model";
import { PU_PER_INCH } from "./units";

/** Geometry closer than this is "the same" — a quarter inch. */
const SAME_TOLERANCE_PU = Math.round(PU_PER_INCH / 4);

export type UpstreamEntityKind = "wall" | "opening" | "object";

/** What the Existing design did. */
export type UpstreamChangeKind = "added" | "changed" | "removed";

export interface UpstreamAction {
  /** Stable per (design, entity): the same discovery is never listed twice. */
  key: string;
  entityKind: UpstreamEntityKind;
  changeKind: UpstreamChangeKind;
  /** Root lineage id of the entity in Existing. */
  rootId: string;
  /** What to do, decided from the plan's own state. */
  disposition: "apply" | "review";
  /** One sentence in the user's language — never lineage or ids. */
  summary: string;
  /** Why the plan is being left alone (review only). */
  reason: string | null;
  /** The entity as Existing now has it (absent for removals). */
  opening?: WallOpening;
  object?: PlanObject;
  /**
   * A wall as Existing now has it, in world coordinates — walls are always a
   * review item, and the answer "use the Existing version" has to be able to
   * build the same wall in the plan.
   */
  wall?: {
    start: { x: number; y: number };
    end: { x: number; y: number };
    kind: "wall" | "boundary";
    thickness: number;
    heightInches: number | null;
    exteriorOverride: boolean | null;
    notes: string;
  };
  /** For openings: the root id of the wall that hosts it in Existing. */
  hostRootWallId?: string;
  /** The entity in the proposed plan this affects, when there is one. */
  targetId?: string;
}

const rootOf = (entity: { id: string; originId: string | null }): string => entity.originId ?? entity.id;

function sameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) <= SAME_TOLERANCE_PU;
}

/** Is the plan's copy still exactly what Existing had before this edit? */
function openingUntouched(descendant: WallOpening, before: WallOpening): boolean {
  return (
    descendant.kind === before.kind &&
    sameNumber(descendant.offsetFromStart, before.offsetFromStart) &&
    sameNumber(descendant.width, before.width) &&
    sameNumber(descendant.height, before.height) &&
    sameNumber(descendant.sillHeight, before.sillHeight) &&
    descendant.swing === before.swing &&
    descendant.windowConfig === before.windowConfig &&
    descendant.windowOperation === before.windowOperation
  );
}

function objectUntouched(descendant: PlanObject, before: PlanObject): boolean {
  return (
    descendant.definitionId === before.definitionId &&
    sameNumber(descendant.x, before.x) &&
    sameNumber(descendant.y, before.y) &&
    sameNumber(descendant.width, before.width) &&
    sameNumber(descendant.depth, before.depth) &&
    descendant.rotationX10 === before.rotationX10
  );
}

function describeOpening(opening: WallOpening): string {
  if (opening.kind === "door") return "a door";
  if (opening.kind === "window") {
    return opening.windowConfig === "single" ? "a window" : `a ${opening.windowConfig} window`;
  }
  return "an opening";
}

/**
 * Decide what one Existing edit means for one proposed plan.
 *
 * `changes` is the changeset the Existing design just committed, so the
 * BEFORE state is available: a plan whose copy still matches that before
 * state has plainly not been touched, and can safely follow. A plan whose
 * copy differs has made a decision, and keeps it.
 */
export function planUpstreamSync(changes: readonly EntityChange[], proposed: PlanModel): UpstreamAction[] {
  const actions: UpstreamAction[] = [];

  const openingsByRoot = new Map<string, WallOpening>();
  for (const opening of proposed.openings.values()) openingsByRoot.set(rootOf(opening), opening);
  const objectsByRoot = new Map<string, PlanObject>();
  for (const object of proposed.objects.values()) objectsByRoot.set(rootOf(object), object);
  const wallsByRoot = new Map<string, string>();
  for (const wall of activeWalls(proposed)) wallsByRoot.set(rootOf(wall), wall.id);

  for (const change of changes) {
    if (change.kind === "opening") {
      const before = change.before as WallOpening | null;
      const after = change.after as WallOpening | null;
      const root = rootOf((after ?? before)!);
      const descendant = openingsByRoot.get(root);

      // ---- something newly discovered in the existing house
      if (!before && after) {
        if (descendant && descendant.archivedAt === null) continue; // already there
        if (descendant && descendant.archivedAt !== null) {
          actions.push({
            key: `opening:${root}`,
            entityKind: "opening",
            changeKind: "added",
            rootId: root,
            disposition: "review",
            summary: `${describeOpening(after)} was added to Existing`,
            reason: "This plan removed it deliberately, so it has been left out.",
            opening: after,
          });
          continue;
        }
        actions.push({
          key: `opening:${root}`,
          entityKind: "opening",
          changeKind: "added",
          rootId: root,
          disposition: "apply",
          summary: `${describeOpening(after)} was added to Existing`,
          reason: null,
          opening: after,
          hostRootWallId: after.wallId,
        });
        continue;
      }

      // ---- a correction to something the plans inherited
      if (before && after && after.archivedAt === null) {
        if (!descendant || descendant.archivedAt !== null) continue; // the plan removed it: its decision
        if (openingUntouched(descendant, before)) {
          actions.push({
            key: `opening:${root}`,
            entityKind: "opening",
            changeKind: "changed",
            rootId: root,
            disposition: "apply",
            summary: `${describeOpening(after)} was corrected in Existing`,
            reason: null,
            opening: after,
            targetId: descendant.id,
          });
        } else {
          actions.push({
            key: `opening:${root}`,
            entityKind: "opening",
            changeKind: "changed",
            rootId: root,
            disposition: "review",
            summary: `${describeOpening(after)} changed in Existing`,
            reason: "This plan already changed it, so the plan's version has been kept.",
            opening: after,
            targetId: descendant.id,
          });
        }
        continue;
      }

      // ---- it turned out not to be there at all
      if (before && after && after.archivedAt !== null) {
        if (!descendant || descendant.archivedAt !== null) continue;
        actions.push({
          key: `opening:${root}`,
          entityKind: "opening",
          changeKind: "removed",
          rootId: root,
          disposition: openingUntouched(descendant, before) ? "apply" : "review",
          summary: `${describeOpening(before)} was removed from Existing`,
          reason: openingUntouched(descendant, before)
            ? null
            : "This plan changed it, so it has been kept until you decide.",
          targetId: descendant.id,
        });
      }
      continue;
    }

    if (change.kind === "object") {
      const before = change.before as PlanObject | null;
      const after = change.after as PlanObject | null;
      const root = rootOf((after ?? before)!);
      const descendant = objectsByRoot.get(root);

      if (!before && after) {
        if (descendant) continue;
        actions.push({
          key: `object:${root}`,
          entityKind: "object",
          changeKind: "added",
          rootId: root,
          disposition: "apply",
          summary: "Something was added to Existing",
          reason: null,
          object: after,
        });
        continue;
      }
      if (before && after && after.archivedAt === null && descendant && descendant.archivedAt === null) {
        actions.push({
          key: `object:${root}`,
          entityKind: "object",
          changeKind: "changed",
          rootId: root,
          disposition: objectUntouched(descendant, before) ? "apply" : "review",
          summary: "Something was changed in Existing",
          reason: objectUntouched(descendant, before)
            ? null
            : "This plan already changed it, so the plan's version has been kept.",
          object: after,
          targetId: descendant.id,
        });
      }
      continue;
    }

    if (change.kind === "wall") {
      const before = change.before as { id: string; originId: string | null; archivedAt: string | null } | null;
      const after = change.after as { id: string; originId: string | null; archivedAt: string | null } | null;
      const root = rootOf((after ?? before)!);
      const inPlan = wallsByRoot.get(root);

      // A wall discovered in the existing house is a real structural
      // difference; it is offered for review rather than pushed into a plan
      // whose layout may deliberately not have it.
      if (!before && after) {
        if (inPlan) continue;
        actions.push({
          key: `wall:${root}`,
          entityKind: "wall",
          changeKind: "added",
          rootId: root,
          disposition: "review",
          summary: "A wall was added to Existing",
          reason: "Adding a wall changes the layout, so it is your call whether this plan gets it.",
        });
        continue;
      }
      if (before && after && after.archivedAt !== null && inPlan) {
        actions.push({
          key: `wall:${root}`,
          entityKind: "wall",
          changeKind: "removed",
          rootId: root,
          disposition: "review",
          summary: "A wall was removed from Existing",
          reason: "This plan still has it — removing a wall changes the layout, so it is your call.",
          targetId: inPlan,
        });
      }
    }
  }

  return actions;
}

/**
 * Where an inherited opening belongs in a proposed plan: on the descendant
 * of the wall that hosts it in Existing. If that wall is not recognisably
 * the same wall any more, we do not guess.
 */
export function hostWallInPlan(proposed: PlanModel, hostRootWallId: string): string | null {
  for (const wall of activeWalls(proposed)) {
    if (rootOf(wall) === hostRootWallId) return wall.id;
  }
  return null;
}

/** Does this plan already carry the entity behind an action? */
export function alreadyPresent(proposed: PlanModel, action: UpstreamAction): boolean {
  if (action.entityKind === "opening") {
    return activeOpenings(proposed).some((o) => rootOf(o) === action.rootId);
  }
  if (action.entityKind === "object") {
    return activeObjects(proposed).some((o) => rootOf(o) === action.rootId);
  }
  return activeWalls(proposed).some((w) => rootOf(w) === action.rootId);
}

/** A plain-language count for the "Existing updated" badge. */
export function describeUpstreamCount(pending: number): string {
  if (pending <= 0) return "";
  return `Existing updated · ${pending} change${pending === 1 ? "" : "s"}`;
}

/** Used by the applier to keep the wall a hosted opening sits on honest. */
export function openingFitsWall(proposed: PlanModel, wallId: string, opening: WallOpening): boolean {
  const wall = proposed.walls.get(wallId);
  if (!wall || wall.archivedAt !== null) return false;
  const a = nodeOf(proposed, wall.startNodeId);
  const b = nodeOf(proposed, wall.endNodeId);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (opening.offsetFromStart < 0 || opening.offsetFromStart + opening.width > length + SAME_TOLERANCE_PU) return false;
  for (const other of activeOpenings(proposed)) {
    if (other.wallId !== wallId) continue;
    const overlaps =
      opening.offsetFromStart < other.offsetFromStart + other.width &&
      other.offsetFromStart < opening.offsetFromStart + opening.width;
    if (overlaps) return false;
  }
  return true;
}
